import type { z } from 'zod';
import {
  zNeighborhoodStoryDef,
  type NeighborhoodStoryDef,
  type NeighborhoodStoryDefInput,
} from './schemas/neighborhood';
import { canonicalStringify, fnv1a32 } from '@/core/util/canonical';
import { checkBalance, type Balance } from './balance';
import type { BoxDefId, ItemId, MiniGameId, RoomId, SetId, UnlockId, WallFloorId } from './ids';
import { zMiniGameDef, type MiniGameDef, type MiniGameDefInput } from './schemas/minigames';
import { zStudioShootDef, type StudioShootDef, type StudioShootDefInput } from './schemas/studio';
import { zRewards, type Reward, type RewardInput } from './schemas/progression';
import {
  zAchievementDef,
  zCosmetics,
  zDialogueDef,
  zLetterDef,
  zMailboxCycle,
  zNpcDef,
  zOrderTemplateDef,
  zPostcardDef,
  zStoryChapterDef,
  zSuggestionDef,
  zUnlockDef,
  type AchievementDef,
  type AchievementDefInput,
  type Cosmetics,
  type CosmeticsInput,
  type DialogueDef,
  type DialogueDefInput,
  type LetterDef,
  type LetterDefInput,
  type MailboxCycle,
  type MailboxCycleInput,
  type NpcDef,
  type NpcDefInput,
  type OrderTemplateDef,
  type OrderTemplateDefInput,
  type PostcardDef,
  type PostcardDefInput,
  type StoryChapterDef,
  type StoryChapterDefInput,
  type SuggestionDef,
  type SuggestionDefInput,
  type UnlockDef,
  type UnlockDefInput,
} from './schemas/progressionDefs';
import {
  zItemDef,
  zSetDef,
  zWallFloorDef,
  type ItemDef,
  type ItemDefInput,
  type SetDef,
  type SetDefInput,
  type WallFloorDef,
  type WallFloorDefInput,
} from './schemas/items';
import {
  zBoxDef,
  zRoomLayout,
  type BoxDef,
  type BoxDefInput,
  type RoomLayout,
  type RoomLayoutInput,
} from './schemas/rooms';

/** Raw content as authored in src/core/content/data (validated here, never trusted as-is). */
export interface RawContent {
  neighborhoodStories?: readonly NeighborhoodStoryDefInput[];
  items: readonly ItemDefInput[];
  sets: readonly SetDefInput[];
  wallFloors: readonly WallFloorDefInput[];
  boxes: readonly BoxDefInput[];
  rooms: readonly RoomLayoutInput[];
  miniGames: readonly MiniGameDefInput[];
  cozinessRewards: Record<RoomId, readonly (readonly RewardInput[])[]>;
  unlocks?: readonly UnlockDefInput[];
  achievements?: readonly AchievementDefInput[];
  suggestions?: readonly SuggestionDefInput[];
  npcs?: readonly NpcDefInput[];
  dialogues?: readonly DialogueDefInput[];
  orderTemplates?: readonly OrderTemplateDefInput[];
  mailbox?: MailboxCycleInput;
  postcards?: readonly PostcardDefInput[];
  letters?: readonly LetterDefInput[];
  chapters?: readonly StoryChapterDefInput[];
  cosmetics?: CosmeticsInput;
  studioShoots?: readonly StudioShootDefInput[];
  studioLevelRewards?: readonly (readonly RewardInput[])[];
  balance: Balance;
}

/** Parsed, indexed, cross-checked content. Domain code receives this as a parameter (no singletons). */
export interface ContentRegistry {
  readonly neighborhoodStories: readonly NeighborhoodStoryDef[];
  readonly version: string;
  readonly balance: Balance;
  readonly items: ReadonlyMap<ItemId, ItemDef>;
  readonly products: readonly ItemDef[];
  readonly sets: ReadonlyMap<SetId, SetDef>;
  readonly wallFloors: ReadonlyMap<WallFloorId, WallFloorDef>;
  readonly rooms: ReadonlyMap<RoomId, RoomLayout>;
  readonly boxes: ReadonlyMap<BoxDefId, BoxDef>;
  readonly miniGames: ReadonlyMap<MiniGameId, MiniGameDef>;
  /** Rewards per coziness level (index 0 = level 1); thresholds are in balance.cozinessLevels. */
  readonly cozinessRewards: Record<RoomId, readonly (readonly Reward[])[]>;
  readonly itemsByTag: ReadonlyMap<string, readonly ItemId[]>;
  readonly unlocks: ReadonlyMap<UnlockId, UnlockDef>;
  /** Unlock ids in dependency order (validated DAG over `unlocked` prerequisites). */
  readonly unlockTopo: readonly UnlockId[];
  readonly achievements: readonly AchievementDef[];
  readonly suggestions: readonly SuggestionDef[];
  readonly npcs: ReadonlyMap<string, NpcDef>;
  readonly dialogues: ReadonlyMap<string, DialogueDef>;
  readonly orderTemplates: ReadonlyMap<string, OrderTemplateDef>;
  readonly mailbox: MailboxCycle | undefined;
  readonly postcards: ReadonlyMap<string, PostcardDef>;
  readonly letters: ReadonlyMap<string, LetterDef>;
  readonly chapters: ReadonlyMap<string, StoryChapterDef>;
  readonly cosmetics: Cosmetics;
  /** Studio NANO bookings (studio-nano.md §4.2). */
  readonly studioShoots: ReadonlyMap<string, StudioShootDef>;
  /** Rewards per Tiếng lành level (index 0 = Lv1); thresholds are in balance.studio.levels. */
  readonly studioLevelRewards: readonly (readonly Reward[])[];
}

export interface ContentIssue {
  path: string;
  message: string;
}

export class ContentError extends Error {
  override name = 'ContentError';
  constructor(readonly issues: readonly ContentIssue[]) {
    super(
      `Invalid content (${String(issues.length)} issue(s)):\n${issues.map((i) => `  ${i.path}: ${i.message}`).join('\n')}`,
    );
  }
}

function parseAll<S extends z.ZodType>(
  schema: S,
  inputs: readonly unknown[],
  label: string,
  issues: ContentIssue[],
): z.output<S>[] {
  const out: z.output<S>[] = [];
  inputs.forEach((input, i) => {
    const id = (input as { id?: unknown }).id;
    const where = `${label}[${typeof id === 'string' ? id : String(i)}]`;
    const result = schema.safeParse(input);
    if (result.success) out.push(result.data);
    else
      for (const issue of result.error.issues)
        issues.push({ path: `${where}.${issue.path.join('.')}`, message: issue.message });
  });
  return out;
}

function indexById<T extends { id: string }>(
  list: readonly T[],
  label: string,
  issues: ContentIssue[],
): Map<T['id'], T> {
  const map = new Map<T['id'], T>();
  for (const entry of list) {
    if (map.has(entry.id)) issues.push({ path: `${label}[${entry.id}]`, message: 'duplicate id' });
    map.set(entry.id, entry);
  }
  return map;
}

/** Parse + index + cross-reference. Throws ContentError listing every problem at once. */
export function buildRegistry(raw: RawContent): ContentRegistry {
  const issues: ContentIssue[] = [];
  const neighborhoodStories = parseAll(
    zNeighborhoodStoryDef,
    raw.neighborhoodStories ?? [],
    'neighborhoodStories',
    issues,
  );
  const items = indexById(parseAll(zItemDef, raw.items, 'items', issues), 'items', issues);
  const sets = indexById(parseAll(zSetDef, raw.sets, 'sets', issues), 'sets', issues);
  const wallFloors = indexById(
    parseAll(zWallFloorDef, raw.wallFloors, 'wallFloors', issues),
    'wallFloors',
    issues,
  );
  const boxes = indexById(parseAll(zBoxDef, raw.boxes, 'boxes', issues), 'boxes', issues);
  const rooms = indexById(parseAll(zRoomLayout, raw.rooms, 'rooms', issues), 'rooms', issues);
  const miniGames = indexById(
    parseAll(zMiniGameDef, raw.miniGames, 'miniGames', issues),
    'miniGames',
    issues,
  );
  for (const mg of miniGames.values()) {
    mg.levels.forEach((l, i) => {
      if (l.level !== i + 1)
        issues.push({ path: `miniGames[${mg.id}]`, message: 'levels must be 1..n in order' });
      for (const r of l.firstClear) {
        if (r.type === 'item' && !items.has(r.itemId))
          issues.push({ path: `miniGames[${mg.id}]`, message: `unknown item ${r.itemId}` });
      }
    });
  }
  for (const message of checkBalance(raw.balance)) issues.push({ path: 'balance', message });
  const cozinessRewards: Record<RoomId, Reward[][]> = { home: [], minimart: [], studio: [] };
  for (const roomId of ['home', 'minimart', 'studio'] as const) {
    const levels = raw.cozinessRewards[roomId];
    if (levels.length !== raw.balance.cozinessLevels[roomId].length) {
      issues.push({
        path: `cozinessRewards.${roomId}`,
        message: 'one reward list per balance.cozinessLevels entry',
      });
    }
    levels.forEach((list, i) => {
      const parsed = zRewards.safeParse(list);
      if (parsed.success) cozinessRewards[roomId].push(parsed.data);
      else
        issues.push({
          path: `cozinessRewards.${roomId}[${String(i)}]`,
          message: parsed.error.issues[0]?.message ?? 'invalid',
        });
    });
  }

  // Cross references.
  for (const set of sets.values()) {
    for (const itemId of set.itemIds) {
      const item = items.get(itemId);
      if (!item) issues.push({ path: `sets[${set.id}]`, message: `unknown item ${itemId}` });
      else if (item.setId !== set.id)
        issues.push({ path: `sets[${set.id}]`, message: `${itemId}.setId is not ${set.id}` });
    }
  }
  for (const item of items.values()) {
    if (item.setId && !sets.get(item.setId)?.itemIds.includes(item.id)) {
      issues.push({
        path: `items[${item.id}]`,
        message: `listed in ${item.setId} but the set does not contain it`,
      });
    }
  }
  for (const box of boxes.values()) {
    for (const reward of box.contents) {
      if (reward.type === 'item' && !items.has(reward.itemId)) {
        issues.push({ path: `boxes[${box.id}]`, message: `unknown item ${reward.itemId}` });
      }
    }
  }
  for (const room of rooms.values()) {
    for (const f of room.fixtures) {
      if (!items.has(f.itemId))
        issues.push({ path: `rooms[${room.id}]`, message: `unknown fixture ${f.itemId}` });
    }
    for (const spawn of room.boxSpawns ?? []) {
      if (!boxes.has(spawn.boxDefId))
        issues.push({ path: `rooms[${room.id}]`, message: `unknown box ${spawn.boxDefId}` });
    }
    for (const wf of [room.defaultWall, room.defaultFloor]) {
      if (!wallFloors.has(wf))
        issues.push({ path: `rooms[${room.id}]`, message: `unknown wall/floor ${wf}` });
    }
  }
  const itemsByTag = new Map<string, ItemId[]>();
  for (const item of items.values()) {
    for (const tag of item.tags) itemsByTag.set(tag, [...(itemsByTag.get(tag) ?? []), item.id]);
  }
  for (const item of items.values()) {
    for (const rule of item.homeTags) {
      for (const atom of rule.all) {
        if (atom.kind === 'near' && !itemsByTag.has(atom.tag)) {
          issues.push({ path: `items[${item.id}].homeTags`, message: `no item provides tag "${atom.tag}"` });
        }
      }
    }
  }
  for (const kind of ['wall', 'floor'] as const) {
    const defaults = [...wallFloors.values()].filter((w) => w.kind === kind && w.isDefault);
    if (wallFloors.size > 0 && defaults.length !== 1) {
      issues.push({ path: 'wallFloors', message: `exactly one default ${kind} required` });
    }
  }

  const unlocks = indexById(parseAll(zUnlockDef, raw.unlocks ?? [], 'unlocks', issues), 'unlocks', issues);
  const achievements = [
    ...indexById(
      parseAll(zAchievementDef, raw.achievements ?? [], 'achievements', issues),
      'achievements',
      issues,
    ).values(),
  ].sort((a, b) => a.order - b.order);
  const suggestions = [
    ...indexById(
      parseAll(zSuggestionDef, raw.suggestions ?? [], 'suggestions', issues),
      'suggestions',
      issues,
    ).values(),
  ].sort((a, b) => a.order - b.order);
  const npcs = indexById(parseAll(zNpcDef, raw.npcs ?? [], 'npcs', issues), 'npcs', issues);
  const dialogues = indexById(
    parseAll(zDialogueDef, raw.dialogues ?? [], 'dialogues', issues),
    'dialogues',
    issues,
  );
  const orderTemplates = indexById(
    parseAll(zOrderTemplateDef, raw.orderTemplates ?? [], 'orderTemplates', issues),
    'orderTemplates',
    issues,
  );
  const postcards = indexById(
    parseAll(zPostcardDef, raw.postcards ?? [], 'postcards', issues),
    'postcards',
    issues,
  );
  const letters = indexById(parseAll(zLetterDef, raw.letters ?? [], 'letters', issues), 'letters', issues);
  const chapters = indexById(
    parseAll(zStoryChapterDef, raw.chapters ?? [], 'chapters', issues),
    'chapters',
    issues,
  );
  let mailbox: MailboxCycle | undefined;
  if (raw.mailbox) {
    const r = zMailboxCycle.safeParse(raw.mailbox);
    if (r.success) mailbox = r.data;
    else issues.push({ path: 'mailbox', message: r.error.issues[0]?.message ?? 'invalid' });
  }
  const emptyCosmetics: Cosmetics = {
    titles: [],
    badges: [],
    stickers: [],
    photoFrames: [],
    avatarParts: [],
    avatarFrames: [],
  };
  let cosmetics = emptyCosmetics;
  if (raw.cosmetics) {
    const r = zCosmetics.safeParse(raw.cosmetics);
    if (r.success) cosmetics = r.data;
    else issues.push({ path: 'cosmetics', message: r.error.issues[0]?.message ?? 'invalid' });
  }

  // Unlock DAG (validator V-DAG): topological order over `unlocked` prerequisites.
  const unlockTopo: UnlockId[] = [];
  {
    const deps = new Map<UnlockId, UnlockId[]>();
    for (const u of unlocks.values()) {
      deps.set(
        u.id,
        u.prerequisites.flatMap((c) => (c.type === 'unlocked' ? [c.unlockId] : [])),
      );
      for (const d of deps.get(u.id) ?? []) {
        if (!unlocks.has(d)) issues.push({ path: `unlocks[${u.id}]`, message: `unknown prerequisite ${d}` });
      }
    }
    const state = new Map<UnlockId, 'visiting' | 'done'>();
    const visit = (id: UnlockId, trail: string[]) => {
      if (state.get(id) === 'done') return;
      if (state.get(id) === 'visiting') {
        issues.push({ path: `unlocks[${id}]`, message: `cycle: ${[...trail, id].join(' → ')}` });
        return;
      }
      state.set(id, 'visiting');
      for (const d of deps.get(id) ?? []) if (unlocks.has(d)) visit(d, [...trail, id]);
      state.set(id, 'done');
      unlockTopo.push(id);
    };
    for (const id of [...unlocks.keys()].sort(
      (a, b) => (unlocks.get(a)?.order ?? 0) - (unlocks.get(b)?.order ?? 0),
    ))
      visit(id, []);
  }
  if (unlocks.size > 0) {
    for (const item of items.values()) {
      if (item.unlockId && !unlocks.has(item.unlockId))
        issues.push({ path: `items[${item.id}]`, message: `unknown unlock ${item.unlockId}` });
    }
  }
  for (const npc of npcs.values()) {
    for (const t of npc.talk)
      if (dialogues.size > 0 && !dialogues.has(t.dialogueId))
        issues.push({ path: `npcs[${npc.id}]`, message: `unknown dialogue ${t.dialogueId}` });
    for (const o of npc.orderTemplates)
      if (!orderTemplates.has(o))
        issues.push({ path: `npcs[${npc.id}]`, message: `unknown order template ${o}` });
  }
  for (const ch of chapters.values()) {
    if (!dialogues.has(ch.dialogueId))
      issues.push({ path: `chapters[${ch.id}]`, message: `unknown dialogue ${ch.dialogueId}` });
  }

  const studioShoots = indexById(
    parseAll(zStudioShootDef, raw.studioShoots ?? [], 'studioShoots', issues),
    'studioShoots',
    issues,
  );
  for (const shoot of studioShoots.values()) {
    const path = `studioShoots[${shoot.id}]`;
    for (const npcId of [shoot.npcId, shoot.model ?? shoot.npcId])
      if (npcs.size > 0 && !npcs.has(npcId)) issues.push({ path, message: `unknown npc ${npcId}` });
    for (const c of shoot.criteria) {
      if (c.type === 'item' && !items.has(c.itemId))
        issues.push({ path, message: `unknown item ${c.itemId}` });
      if (c.type === 'tag' && !itemsByTag.has(c.tag))
        issues.push({ path, message: `no item has tag ${c.tag}` });
      if (c.type === 'wallFloor')
        for (const id of c.ids)
          if (!wallFloors.has(id)) issues.push({ path, message: `unknown wall/floor ${id}` });
    }
  }

  const studioLevelRewards: Reward[][] = [];
  const rawStudioLevels = raw.studioLevelRewards ?? [];
  if (rawStudioLevels.length > 0 && rawStudioLevels.length !== raw.balance.studio.levels.length)
    issues.push({ path: 'studioLevelRewards', message: 'one reward list per balance.studio.levels entry' });
  rawStudioLevels.forEach((list, i) => {
    const parsed = zRewards.safeParse(list);
    if (parsed.success) studioLevelRewards.push(parsed.data);
    else
      issues.push({
        path: `studioLevelRewards[${String(i)}]`,
        message: parsed.error.issues[0]?.message ?? 'invalid',
      });
  });

  const storyIds = new Set(neighborhoodStories.map((s) => s.id));
  if (storyIds.size !== neighborhoodStories.length)
    issues.push({ path: 'neighborhoodStories', message: 'duplicate story id' });
  for (const story of neighborhoodStories) {
    const path = `neighborhoodStories[${story.id}]`;
    if (!sets.has(story.setId)) issues.push({ path, message: `unknown set ${story.setId}` });
    if (!letters.has(story.inviteLetterId))
      issues.push({ path, message: `unknown letter ${story.inviteLetterId}` });
    if (!items.has(story.keepsakeItemId))
      issues.push({ path, message: `unknown keepsake ${story.keepsakeItemId}` });
    if (story.requiresStory && (!storyIds.has(story.requiresStory) || story.requiresStory === story.id))
      issues.push({ path, message: 'invalid prerequisite story' });
    for (const supply of story.supplies) {
      const product = items.get(supply.itemId);
      if (product?.kind !== 'product' || (product.unlockId && product.unlockId !== 'unl_minimart'))
        issues.push({ path, message: `supplies must be starter stock: ${supply.itemId}` });
    }
    for (const step of story.steps)
      for (const reward of step.rewards)
        if (reward.type === 'item' && !items.has(reward.itemId))
          issues.push({ path, message: `unknown reward ${reward.itemId}` });
  }
  for (const entry of [...items.values(), ...wallFloors.values()])
    if (entry.storyGate && !storyIds.has(entry.storyGate.storyId))
      issues.push({ path: entry.id, message: 'unknown story gate' });

  if (issues.length > 0) throw new ContentError(issues);

  return {
    neighborhoodStories,
    version: fnv1a32(canonicalStringify(raw)),
    balance: raw.balance,
    items,
    products: [...items.values()]
      .filter((i) => i.kind === 'product')
      .sort((a, b) => a.id.localeCompare(b.id)),
    sets,
    wallFloors,
    rooms,
    boxes,
    miniGames,
    cozinessRewards,
    itemsByTag,
    unlocks,
    unlockTopo,
    achievements,
    suggestions,
    npcs,
    dialogues,
    orderTemplates,
    mailbox,
    postcards,
    letters,
    chapters,
    cosmetics,
    studioShoots,
    studioLevelRewards,
  };
}
